/**
 * 历史系统（DESIGN.md §4.5）。
 *
 * **混合策略**，因为两种纯粹做法都不可行：
 * - 纯命令重放：一次大画笔笔画重放起来太慢，撤销会有可感知的延迟。
 * - 纯快照：每步存整个文档的像素，几步就爆内存。
 *
 * 所以按操作性质分开：
 * - **结构性操作**（增删图层、改属性）存参数逆操作，每条几十字节。
 * - **像素操作**（画笔、滤镜）存 tile 差分——只记**实际改动的块**，
 *   靠 M1-02 的 COW 快照实现，快照本身不复制像素。
 *
 * 两条容易被做错的规则：
 *
 * 1. **一次笔画是一条历史**，不是几百条。pointerdown 到 pointerup 之间的所有
 *    像素改动合并成一个条目——否则用户按一次 Ctrl+Z 只撤掉笔画的最后一小段。
 * 2. **超预算时压平最老的条目**，而不是拒绝新操作。压平后那些步骤不可再撤销，
 *    此时 `canUndo` 必须如实返回 false，而不是让 undo 抛错或回到错误状态。
 */
import type { Command } from './command-bus';
import type { Document } from '../document/types';
import { BYTES_PER_TILE, type TileStore } from '../engine/tile/store';
import type { TileSnapshot } from '../engine/tile/cow';
import { tileKeyString, type TileKey } from '../engine/tile/key';

/** 一条历史记录。 */
export interface HistoryEntry {
  readonly id: number;
  readonly label: string;
  /** 撤销到本条之前的状态所需的命令。 */
  readonly inverse: Command | null;
  /** 本条执行后的文档。撤销/重做直接换引用，无需重放。 */
  readonly document: Document;
  /** 像素差分。结构性操作为 null。 */
  readonly pixels: PixelDiff | null;
  /** 内存开销估算（字节）。 */
  readonly cost: number;
}

/**
 * 像素差分（双向，M3-07）。
 *
 * 持有 COW 快照与受影响的键集合。快照不复制像素，
 * 只在这些块**之后被写入**时才发生克隆——所以历史条目的实际内存开销
 * 等于"改动的块数 × 块大小"，而不是整图。
 *
 * `after` 是 redo 用的「改动后」快照，**首次 undo 时惰性捕获**：
 * 从不撤销的操作零额外成本；撤销过的操作多付一份键集的 COW 引用
 * （同样只在被覆写时才真正克隆）。M1-08 原设想"redo 靠重放笔画"，
 * 但重放需要每种像素操作都保存并回放它的参数流；双向快照对所有像素
 * 操作一视同仁，成本仍在 §4.5 的 tile 差分模型内。
 */
export interface PixelDiff {
  readonly snapshot: TileSnapshot;
  readonly keys: readonly TileKey[];
  /** redo 快照。undo 时由 History 惰性填充；计入内存统计。 */
  after?: TileSnapshot;
}

export interface HistoryOptions {
  readonly store: TileStore;
  /** 历史内存预算（字节）。§4.5 建议 512 MB。 */
  readonly budgetBytes?: number;
  /** 条目数上限。 */
  readonly maxEntries?: number;
}

export interface HistoryStats {
  readonly entries: number;
  readonly bytes: number;
  /** 已被压平进基线、不可再撤销的条目数。 */
  readonly flattened: number;
  readonly canUndo: boolean;
  readonly canRedo: boolean;
}

/**
 * 一次连续像素操作的记录器。
 *
 * 用法：`begin()` → 多次 `touch()` → `end()`。整段合并为一条历史。
 * 这正是"一次画笔笔画一条历史"的实现方式。
 */
export class PixelTransaction {
  readonly #keys = new Map<string, TileKey>();
  #snapshot: TileSnapshot | null = null;
  #ended = false;

  constructor(
    private readonly store: TileStore,
    readonly label: string,
  ) {}

  /**
   * 声明一块将被修改。
   *
   * 必须在**实际写入之前**调用——快照要拍的是修改前的内容。
   * 写完再声明拿到的是新值，撤销会回到错误的状态。
   */
  touch(key: TileKey): void {
    if (this.#ended) throw new Error('事务已结束，不能再声明新的块。');
    const id = tileKeyString(key);
    if (this.#keys.has(id)) return;
    this.#keys.set(id, key);
  }

  /** 拍下所有已声明块的快照。在第一次写入之前调用。 */
  capture(): void {
    if (this.#snapshot !== null) return;
    this.#snapshot = this.store.snapshot(this.#keys.values());
  }

  get touchedCount(): number {
    return this.#keys.size;
  }

  /** 结束事务，产出差分。没有任何块被改动时返回 null。 */
  end(): PixelDiff | null {
    this.#ended = true;
    if (this.#keys.size === 0) return null;
    this.capture();
    return { snapshot: this.#snapshot!, keys: [...this.#keys.values()] };
  }
}

/** §4.5/§8 建议的历史内存预算默认值——`HistoryOptions.budgetBytes` 与
 * 性能面板（M5-01）的显示都引用这一个常量，不各自硬编码 512MB 两份。 */
export const DEFAULT_HISTORY_BUDGET_BYTES = 512 * 1024 * 1024;

export class History {
  readonly #store: TileStore;
  readonly #budget: number;
  readonly #maxEntries: number;
  readonly #entries: HistoryEntry[] = [];

  /** 当前位置。指向"最后一条已应用的条目"的下标，-1 表示在基线。 */
  #cursor = -1;
  /** 基线文档：所有已压平条目之后的状态。撤销不能越过它。 */
  #baseline: Document;
  #flattened = 0;
  #nextId = 1;

  constructor(baseline: Document, options: HistoryOptions) {
    this.#baseline = baseline;
    this.#store = options.store;
    this.#budget = options.budgetBytes ?? DEFAULT_HISTORY_BUDGET_BYTES;
    this.#maxEntries = options.maxEntries ?? 100;
  }

  get current(): Document {
    return this.#cursor < 0 ? this.#baseline : this.#entries[this.#cursor]!.document;
  }

  /**
   * 能否撤销。
   *
   * 压平之后到达基线就不能再退——此时如实返回 false，
   * 而不是让 undo 抛错或把文档带到一个错误的状态。
   */
  get canUndo(): boolean {
    return this.#cursor >= 0;
  }

  get canRedo(): boolean {
    return this.#cursor < this.#entries.length - 1;
  }

  /** 记录一条历史。 */
  push(options: {
    label: string;
    inverse: Command | null;
    document: Document;
    pixels?: PixelDiff | null;
    structuralCost?: number;
  }): HistoryEntry {
    // 在中间位置执行新操作时，后面的重做分支被丢弃——这是所有编辑器的惯例，
    // 保留分支需要树形历史，而那会让 UI 复杂到得不偿失
    this.#discardRedoBranch();

    const pixels = options.pixels ?? null;
    const entry: HistoryEntry = {
      id: this.#nextId++,
      label: options.label,
      inverse: options.inverse,
      document: options.document,
      pixels,
      cost: pixels === null ? (options.structuralCost ?? 128) : pixels.keys.length * BYTES_PER_TILE,
    };

    this.#entries.push(entry);
    this.#cursor = this.#entries.length - 1;
    this.#enforceBudget();
    return entry;
  }

  /** 丢弃当前位置之后的条目（重做分支）。 */
  #discardRedoBranch(): void {
    while (this.#entries.length > this.#cursor + 1) {
      const dropped = this.#entries.pop();
      dropped?.pixels?.snapshot.release();
      dropped?.pixels?.after?.release();
    }
  }

  /**
   * 撤销一步。
   *
   * 直接换文档引用而非重放命令——文档是不可变的，每条历史都持有它执行后的
   * 完整状态（结构共享，开销只是几个对象引用）。像素则从快照恢复。
   *
   * @returns 撤销后的文档；已在基线时返回 null。
   */
  undo(): Document | null {
    if (!this.canUndo) return null;

    const entry = this.#entries[this.#cursor]!;
    // 像素回滚：把快照里的内容写回 TileStore。
    // 回滚前先拍「改动后」快照（若还没有）——redo 的数据源（M3-07）
    if (entry.pixels !== null) {
      entry.pixels.after ??= this.#store.snapshot(entry.pixels.keys.values());
      this.#store.restore(entry.pixels.snapshot, entry.pixels.keys);
    }

    this.#cursor--;
    return this.current;
  }

  /** 重做一步。像素从 undo 时捕获的 after 快照恢复，逐字节一致。 */
  redo(): Document | null {
    if (!this.canRedo) return null;
    this.#cursor++;
    const entry = this.#entries[this.#cursor]!;
    if (entry.pixels?.after !== undefined) {
      this.#store.restore(entry.pixels.after, entry.pixels.keys);
    }
    return entry.document;
  }

  /**
   * 删除某条历史（含它自己）及其后所有条目——对应 PS 历史面板垃圾桶
   * "删除这个状态"的语义：删掉的部分永远不能再重做，跟普通 undo（不丢数据、
   * 仍可 redo）是两回事。`index` 是 `entries` 数组下标。
   *
   * 游标当前落在待删范围内（含相等）时不能只挪游标了事——那样画面还停在
   * 马上要被永久释放快照的状态上。复用 `undo()` 现成的"从快照恢复
   * TileStore"逻辑先把游标真的退到删除范围之前，再释放并移除条目。
   *
   * @returns 删除后应该展示的文档；index 越界（不在 `[0, entries.length)`）
   * 时什么都不做，返回 null。
   */
  deleteFrom(index: number): Document | null {
    if (index < 0 || index >= this.#entries.length) return null;
    let current: Document | null = null;
    while (this.#cursor >= index) current = this.undo();

    const dropped = this.#entries.splice(index);
    for (const entry of dropped) {
      entry.pixels?.snapshot.release();
      entry.pixels?.after?.release();
    }
    return current ?? this.current;
  }

  /**
   * 让历史回到预算内。
   *
   * 从**最老**的条目开始压平进基线：那些步骤此后不可再撤销。
   * 压平而不是拒绝新操作——用户正在编辑，拒绝比丢掉很久以前的撤销点糟得多。
   */
  #enforceBudget(): void {
    while (
      this.#entries.length > this.#maxEntries ||
      (this.totalBytes > this.#budget && this.#entries.length > 1)
    ) {
      const oldest = this.#entries.shift();
      if (oldest === undefined) break;

      // 基线推进到这条之后的状态
      this.#baseline = oldest.document;
      oldest.pixels?.snapshot.release();
      oldest.pixels?.after?.release();
      this.#cursor--;
      this.#flattened++;
    }
    // 游标不能小于 -1（-1 即基线）
    if (this.#cursor < -1) this.#cursor = -1;
  }

  get totalBytes(): number {
    let bytes = 0;
    for (const entry of this.#entries) {
      bytes += entry.cost;
      // after 快照是 undo 时追加的成本，一样要受预算约束
      if (entry.pixels?.after !== undefined) bytes += entry.pixels.keys.length * BYTES_PER_TILE;
    }
    return bytes;
  }

  /** 历史条目列表，供历史面板显示。 */
  get entries(): readonly HistoryEntry[] {
    return this.#entries;
  }

  get cursor(): number {
    return this.#cursor;
  }

  stats(): HistoryStats {
    return {
      entries: this.#entries.length,
      bytes: this.totalBytes,
      flattened: this.#flattened,
      canUndo: this.canUndo,
      canRedo: this.canRedo,
    };
  }

  /** 清空历史并以给定文档为新基线。 */
  reset(baseline: Document): void {
    for (const entry of this.#entries) {
      entry.pixels?.snapshot.release();
      entry.pixels?.after?.release();
    }
    this.#entries.length = 0;
    this.#cursor = -1;
    this.#baseline = baseline;
  }
}
